import React, { useMemo } from 'react';
import {
  Card, CardTitle, Badge, Typography, Button, CardBody,
  Link
} from '@one-thd/sui-atomic-components';
import { DataGrid, DataGridCell } from '@thd-olt-component-react/data-grid';
import { Apron } from '@one-thd/sui-icons';
import { bool, arrayOf, shape } from 'prop-types';
import { PROJECT_STATUS_ACTIVE, PROJECT_STATUS_ARCHIVED, PROJECT_ENTRY_POINT } from '../project-summary-helper';
import { dateModifiedMessage, triggerAnalytics } from '../../helpers';

export const FullStateCard = ({ loading, projects, shouldShowBadge }) => {

  const columns = [
    {
      title: 'Project Name',
      key: 'projectName',
      align: 'left',
      renderCell: ({ item }) => (
        <DataGridCell>
          <div className="sui-p-2">
            <Typography truncate>
              <Link
                href={item.url}
                truncate
                onClick={() => triggerAnalytics(PROJECT_ENTRY_POINT, { entryPoint: 'project name' })}
              >{item.projectName}
              </Link>
            </Typography>
          </div>
        </DataGridCell>
      ),
    },
    {
      title: 'Project  Number',
      key: 'projectNumber',
      align: 'left',
      renderCell: ({ item }) => (
        <DataGridCell>{item.projectNumber}</DataGridCell>
      ),
    },
    {
      title: 'Project Status',
      key: 'projectStatus',
      align: 'left',
      renderCell: ({ item }) => (
        <DataGridCell>
          <Badge
            data-testid="badge-status"
            size="small"
            variant="subtle"
            color={item.projectStatus === PROJECT_STATUS_ACTIVE ? 'info' : 'medium'}
          >
            <span className="sui-capitalize">
              {item.projectStatus === PROJECT_STATUS_ACTIVE ? PROJECT_STATUS_ACTIVE : PROJECT_STATUS_ARCHIVED}
            </span>
          </Badge>
        </DataGridCell>
      ),
    },
    {
      title: 'Last Modified',
      key: 'lastModified',
      align: 'left',
      renderCell: ({ item }) => (
        <DataGridCell>
          <Typography>{item.lastModified}</Typography>
        </DataGridCell>
      ),
    },
    {
      title: 'Created By',
      key: 'createdBy',
      align: 'left',
      renderCell: ({ item }) => (
        <DataGridCell>
          {item.isCreatedByTHDAssociate
            ? (
              <div className="sui-flex sui-items-baseline">
                <div className="sui-mr-2"><Apron color="brand" size="small" /></div>
                <Typography>THD Associate</Typography>
              </div>
            )
            : <Typography>{item.createdBy}</Typography>}
        </DataGridCell>
      ),
    },

  ];

  const visibleProjects = useMemo(() => (projects || []).map((project) => ({
    id: project.id,
    projectName: project.projectName,
    projectNumber: project.projectId,
    projectStatus: project.status,
    lastModified: dateModifiedMessage(project.modifyDateTime),
    createdBy: project.createdByName,
    dropdown: '',
    isCreatedByTHDAssociate: project.isCreatedByTHDAssociate,
    url: `/projects/${project.id}`
  })).slice(0, 6), [projects]);

  return (
    <Card grow PaperProps={{ className: 'sui-flex' }}>
      <CardTitle
        header={(
          <>
            {shouldShowBadge && (
              <div className="sui-mb-2">
                <Badge data-testid="beta-badge" variant="regular" color="info">Beta</Badge>
              </div>
            )}
            <Typography data-testid="card-title" height="tight" variant="h3" weight="bold">
              Projects
            </Typography>
          </>
        )}
        action={(
          <Button
            variant="text"
            href="/projects"
            onClick={() => triggerAnalytics(PROJECT_ENTRY_POINT, { entryPoint: 'view all' })}
          >View All
          </Button>
        )}
      />
      <CardBody>
        <DataGrid
          columns={columns}
          data={visibleProjects}
          loading={loading}
          skeletonRowCount={6}
          layout="fixed"
          dataId="id"
        />
      </CardBody>
    </Card>
  );
};

FullStateCard.propTypes = {
  projects: arrayOf(shape({})),
  loading: bool,
  shouldShowBadge: bool
};

FullStateCard.defaultProps = {
  projects: [],
  loading: false,
  shouldShowBadge: false
};
